/**
 * useGuestMode Hook
 * Central hook for managing guest mode behavior across the app
 */

import { useState, useEffect, useCallback } from 'react';
import GuestModeManager from '../firebase/guestModeManager';
import { useAuth } from '../contexts/AuthContext';
import React from 'react';
import { RegistrationGate, InlineRegistrationPrompt, GuestModeBanner } from '../components/auth/RegistrationGate';
import { useMigrationPreview } from '../firebase/guestMigrationService';

export const useGuestMode = () => {
  const { currentUser: user, loading: authLoading } = useAuth(); // ← FIXED!
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [storageInfo, setStorageInfo] = useState(GuestModeManager.getStorageInfo());
  const [shouldPromptRegistration, setShouldPromptRegistration] = useState(false);
  const [promptReason, setPromptReason] = useState<'DRAFT_LIMIT' | 'WORD_COUNT' | 'XP_MILESTONE' | null>(null);

  useEffect(() => {
    // User is in guest mode if not authenticated
    setIsGuestMode(!user && !authLoading);
  }, [user, authLoading]);

  useEffect(() => {
    // Update storage info periodically
    const interval = setInterval(() => {
      setStorageInfo(GuestModeManager.getStorageInfo());
      
      // Check if we should prompt registration
      const promptCheck = GuestModeManager.shouldPromptRegistration();
      if (promptCheck.shouldPrompt && isGuestMode) {
        setShouldPromptRegistration(true);
        setPromptReason(promptCheck.reason || null);
      }
    }, 5000); // Check every 5 seconds

    return () => clearInterval(interval);
  }, [isGuestMode]);

  const saveDraft = useCallback(async (draftData: any) => {
    if (user) {
      // User is authenticated - save to Firebase
      // Use your existing Firebase save logic here
      return; // Return Firebase document ID
    }

    // Guest mode - save to localStorage
    try {
      const draftId = GuestModeManager.saveDraft(draftData);
      setStorageInfo(GuestModeManager.getStorageInfo());
      return draftId;
    } catch (error) {
      if (error instanceof Error && error.message === 'GUEST_LIMIT_REACHED') {
        // Trigger hard registration gate
        throw error;
      }
      throw error;
    }
  }, [user]);

  const updateDraft = useCallback(async (draftId: string, updates: any) => {
    if (user) {
      // Update in Firebase
      // Use your existing Firebase update logic
      return;
    }

    // Update in localStorage
    GuestModeManager.updateDraft(draftId, updates);
    setStorageInfo(GuestModeManager.getStorageInfo());
  }, [user]);

  const getDrafts = useCallback(() => {
    if (user) {
      // Fetch from Firebase
      // Return your Firebase drafts
      return [];
    }

    return GuestModeManager.getAllDrafts();
  }, [user]);

  const dismissPrompt = useCallback(() => {
    setShouldPromptRegistration(false);
    setPromptReason(null);
  }, []);

  return {
    isGuestMode,
    storageInfo,
    saveDraft,
    updateDraft,
    getDrafts,
    shouldPromptRegistration,
    promptReason,
    dismissPrompt
  };
};

/**
 * Example: Story Editor with Deferred Registration
 */



export const StoryEditorExample: React.FC = () => {
  const { currentUser: user } = useAuth();
  const {
    isGuestMode,
    storageInfo,
    saveDraft,
    updateDraft,
    shouldPromptRegistration,
    promptReason,
    dismissPrompt
  } = useGuestMode();

  const [showPublishGate, setShowPublishGate] = useState(false);
  const [showLimitGate, setShowLimitGate] = useState(false);
  const [storyTitle, setStoryTitle] = useState('');
  const [storyContent, setStoryContent] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);

  // Auto-save functionality (works for both guest and authenticated)
  useEffect(() => {
    const autoSave = setTimeout(() => {
      if (storyContent.trim()) {
        handleSave();
      }
    }, 3000); // Auto-save after 3 seconds of inactivity

    return () => clearTimeout(autoSave);
  }, [storyContent, storyTitle]);

  const handleSave = async () => {
    try {
      const draftData = {
        title: storyTitle,
        content: storyContent,
        wordCount: wordCount
      };

      if (currentDraftId) {
        await updateDraft(currentDraftId, draftData);
      } else {
        const draftId = await saveDraft(draftData);
        setCurrentDraftId(draftId as string);
      }

      // Show save indicator
      console.log('Draft saved!');
    } catch (error) {
      if (error instanceof Error && error.message === 'GUEST_LIMIT_REACHED') {
        setShowLimitGate(true);
      }
    }
  };

  const handlePublish = () => {
    if (isGuestMode) {
      // Gate registration at publish
      setShowPublishGate(true);
    } else {
      // User is authenticated, proceed with publish
      publishStory();
    }
  };

  const publishStory = async () => {
    // Your existing publish logic
    console.log('Publishing story...');
  };

  const handleRegister = () => {
    // Navigate to registration with migration context
    window.location.href = '/register?migrate=true';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Guest Mode Banner - only show for guests */}
      {isGuestMode && <GuestModeBanner onRegister={handleRegister} />}

      {/* Inline Registration Prompt - subtle encouragement */}
      {shouldPromptRegistration && promptReason && (
        <div className="max-w-4xl mx-auto px-4 pt-4">
          <InlineRegistrationPrompt
            reason={promptReason}
            onRegister={handleRegister}
            onDismiss={dismissPrompt}
          />
        </div>
      )}

      {/* Editor */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-6">
          {/* Title Input */}
          <input
            type="text"
            placeholder="Story Title"
            value={storyTitle}
            onChange={(e) => setStoryTitle(e.target.value)}
            className="w-full text-3xl font-bold border-none outline-none mb-4"
          />

          {/* Content Editor */}
          <textarea
            placeholder="Start writing your story..."
            value={storyContent}
            onChange={(e) => {
              setStoryContent(e.target.value);
              setWordCount(e.target.value.split(/\s+/).filter(Boolean).length);
            }}
            className="w-full min-h-[400px] border-none outline-none resize-none"
          />

          {/* Stats Bar */}
          <div className="flex justify-between items-center pt-4 border-t border-gray-200 text-sm text-gray-600">
            <div>
              {wordCount} words
              {isGuestMode && (
                <span className="ml-4">
                  • {storageInfo.draftCount}/{storageInfo.maxDrafts} drafts saved
                </span>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleSave}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                💾 Save
              </button>
              <button
                onClick={handlePublish}
                className="px-6 py-2 bg-purple-500 text-white rounded-lg font-semibold hover:bg-purple-600 transition-colors"
              >
                📚 Publish
              </button>
            </div>
          </div>
        </div>

        {/* Guest Mode Explainer */}
        {isGuestMode && (
          <div className="mt-4 text-center text-sm text-gray-600">
            ℹ️ Your story is saved on this device. 
            <button 
              onClick={handleRegister}
              className="text-purple-600 hover:underline ml-1"
            >
              Sign up free
            </button>
            {' '}to save to the cloud and publish.
          </div>
        )}
      </div>

      {/* Registration Gates */}
      {showPublishGate && (
        <RegistrationGate
          type="publish"
          draftTitle={storyTitle}
          onRegister={handleRegister}
          onCancel={() => setShowPublishGate(false)}
        />
      )}

      {showLimitGate && (
        <RegistrationGate
          type="limit-reached"
          onRegister={handleRegister}
        />
      )}
    </div>
  );
};

/**
 * Example: Registration Page with Migration Preview
 */

export const RegistrationPageExample: React.FC = () => {
  const { hasGuestData, preview, draftCount, totalXP, totalWords } = useMigrationPreview();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
        <h1 className="text-3xl font-bold text-center mb-6">
          Join Scriblish!
        </h1>

        {/* Migration Preview - show guest data that will be saved */}
        {hasGuestData && preview && (
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-lg p-4 mb-6">
            <h3 className="font-bold text-lg mb-3 text-center">
              🎉 We'll Save Your Progress!
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Stories:</span>
                <strong>{draftCount} drafts</strong>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Words Written:</span>
                <strong>{totalWords.toLocaleString()}</strong>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">XP Earned:</span>
                <strong>{totalXP.toLocaleString()} XP</strong>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-purple-200">
              <div className="text-xs text-gray-600">
                Your latest drafts:
              </div>
              {preview.drafts.slice(0, 3).map((draft, idx) => (
                <div key={idx} className="text-xs text-gray-700 mt-1">
                  • {draft.title} ({draft.wordCount} words)
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Registration Form */}
        <form className="space-y-4">
          {/* Your registration form fields */}
          <div>
            <label className="block text-sm font-medium mb-1">Email</label>
            <input
              type="email"
              className="w-full px-4 py-2 border rounded-lg"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Password</label>
            <input
              type="password"
              className="w-full px-4 py-2 border rounded-lg"
              placeholder="••••••••"
            />
          </div>

          <button
            type="submit"
            className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold py-3 rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all"
          >
            {hasGuestData ? 'Create Account & Save Progress' : 'Create Free Account'}
          </button>
        </form>

        <p className="text-xs text-gray-500 text-center mt-4">
          Free forever • No credit card • COPPA compliant
        </p>
      </div>
    </div>
  );
};